import React, { useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    Chip,
    Stack,
    CircularProgress,
    Button,
    Grid,
    Paper,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetRole } from '../../hooks/roles/useGetRole';
import { useTranslation } from 'react-i18next';

const ModelShowRole = ({ open, onClose, roleId }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';

    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const { Role, isLoading } = useGetRole(roleId);
    const role = Role?.data;

    // تجميع الصلاحيات حسب الـ group
    const groupedPermissions = useMemo(() => {
        if (!role?.permissions) return {};
        return role.permissions.reduce((acc, perm) => {
            const groupKey = perm.group?.label || 'other';
            if (!acc[groupKey]) acc[groupKey] = [];
            acc[groupKey].push(perm);
            return acc;
        }, {});
    }, [role]);

    const groupKeys = Object.keys(groupedPermissions);

    const formatGroupLabel = (groupKey) => {
        if (!groupKey || groupKey === 'other') {
            return isArabic ? 'أخرى' : 'Other';
        }
        return groupKey.charAt(0).toUpperCase() + groupKey.slice(1);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: theme.palette.boxShadow,
                    bgcolor: theme.palette.background.card,
                    '&::-webkit-scrollbar': {
                        width: 6,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(148,163,184,0.6)',
                        borderRadius: 999,
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(148,163,184,0.9)',
                    },
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(148,163,184,0.8) transparent',
                },
            }}
        >
            <DialogTitle
                sx={{
                    m: 0,
                    px: 3,
                    py: 2,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                    bgcolor: isDark
                        ? 'rgba(80,87,255,0.08)'
                        : 'rgba(80,87,255,0.04)',
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {isArabic ? 'تفاصيل الدور' : 'Role details'}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                bgcolor: isDark
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'rgba(0,0,0,0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    pt: 2,
                    pb: 3,
                    px: 3,
                    direction: isArabic ? 'rtl' : 'ltr',
                    bgcolor: theme.palette.background.form,
                }}
            >
                {isLoading || !roleId ? (
                    <Box
                        sx={{
                            py: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress size={32} />
                    </Box>
                ) : !role ? (
                    <Typography align="center" sx={{ py: 4 }}>
                        {isArabic
                            ? 'لا توجد بيانات متاحة لهذا الدور'
                            : 'No data available for this role'}
                    </Typography>
                ) : (
                    <Box>
                        <Box
                            sx={{
                                mb: 3,
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    flex: 1,
                                    borderRadius: 2,
                                    borderColor: theme.palette.divider,
                                    bgcolor: isDark
                                        ? 'rgba(255,255,255,0.02)'
                                        : 'rgba(255,255,255,0.9)',
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Stack direction="row" spacing={1}>
                                        <Typography variant="body2" color="text.secondary">
                                            {isArabic ? 'الاسم:' : 'Name:'}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {role.name}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {isArabic ? 'عدد الصلاحيات:' : 'Permissions count:'}
                                        </Typography>
                                        <Chip
                                            label={role.permissions?.length || 0}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Box>

                        {/* الصلاحيات مجمّعة حسب الجروب */}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {isArabic ? 'الصلاحيات حسب الوحدات' : 'Permissions by module'}
                            </Typography>

                            {(!role.permissions || role.permissions.length === 0) ? (
                                <Typography variant="body2" color="text.secondary">
                                    {isArabic
                                        ? 'لا توجد صلاحيات مرتبطة بهذا الدور.'
                                        : 'No permissions attached to this role.'}
                                </Typography>
                            ) : (
                                <Box
                                    sx={{
                                        maxHeight: 450,
                                        overflowY: 'auto',
                                        pr: 0.5,
                                    }}
                                >
                                    <Grid container >
                                        {groupKeys.map((groupKey) => {
                                            const perms = groupedPermissions[groupKey];
                                            return (
                                                <Grid item xs={12} md={6} key={groupKey}>
                                                    <Paper
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 1,
                                                            borderRadius: 0,
                                                            borderColor: theme.palette.divider,
                                                            bgcolor: isDark
                                                                ? 'rgba(255,255,255,0.03)'
                                                                : 'rgba(255,255,255,0.96)',
                                                        }}
                                                    >
                                                        {/* عنوان الجروب */}
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            sx={{ mb: 1 }}
                                                        >
                                                            <Typography variant="subtitle2" fontWeight={600}>
                                                                {formatGroupLabel(groupKey)}
                                                            </Typography>
                                                            <Chip
                                                                label={perms.length}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        </Stack>

                                                        {/* قائمة الصلاحيات داخل الجروب */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: 0.75,
                                                            }}
                                                        >
                                                            {perms.map((perm) => (
                                                                <Chip
                                                                    key={perm.id}
                                                                    label={perm.label || perm.name}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        borderRadius: 999,
                                                                        borderColor: isDark
                                                                            ? '#616161'
                                                                            : 'rgba(80,87,255,0.5)',
                                                                        // bgcolor: isDark
                                                                        //     ? 'rgba(80,87,255,0.12)'
                                                                        //     : 'rgba(80,87,255,0.06)',
                                                                        color: theme.palette.text.primary,
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 1.5,
                    direction: isArabic ? 'rtl' : 'ltr',
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                    bgcolor: isDark
                        ? 'rgba(255,255,255,0.02)'
                        : 'rgba(0,0,0,0.02)',
                }}
            >
                <Button onClick={onClose} variant="contained" color="primary">
                    {isArabic ? 'إغلاق' : 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModelShowRole;
